/**
 * Iter 191 — Strategy Change Feed
 *
 * Cross-brand roll-up of content-strategy revisions (manual overrides,
 * carry-overs, AI regenerations) for the Portfolio Pulse dashboard.
 * Helps account managers spot strategies drifting from their AI baseline.
 *
 * Click any brand → opens an inline mini-drawer with full revision timeline
 * (no page navigation, per user preference).
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import {
  ScrollText, PenSquare, ArrowLeftRight, Bot, User as UserIcon, X,
  ChevronRight, ShieldCheck, Users,
} from "lucide-react";
import { fmtRelative } from "@/lib/format";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";

export default function StrategyChangeFeedCard({ wsId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [drawerBrandId, setDrawerBrandId] = useState(null);
  const [drawerBrandName, setDrawerBrandName] = useState("");

  useEffect(() => {
    if (!wsId) return;
    setLoading(true);
    api.get(`/workspaces/${wsId}/portfolio/strategy-change-feed`,
              { params: { days } })
      .then(r => setData(r.data))
      .catch(e => { toast.error(apiErrorMessage(e, "Failed to load feed")); setData(null); })
      .finally(() => setLoading(false));
  }, [wsId, days]);

  const sparklineMax = useMemo(() => {
    if (!data?.series) return 1;
    return Math.max(1, ...data.series.map(d => d.manual + d.carry_over + d.ai_regens));
  }, [data]);

  if (loading) return (
    <div className="border border-border bg-card p-3 text-xs text-muted-foreground"
              data-testid="portfolio-strategy-change-feed">
      Loading strategy change feed…
    </div>
  );
  if (!data) return null;

  const s = data.summary || {};
  const isQuiet = (s.manual + s.carry_over + s.ai_regens) === 0;

  return (
    <div className="border border-border bg-card p-3"
              data-testid="portfolio-strategy-change-feed">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1.5">
            <ScrollText className="w-3.5 h-3.5" /> Strategy change feed
          </div>
          <div className="text-[10px] text-muted-foreground">
            {isQuiet
              ? "No strategy edits in this window — nothing to check on."
              : `${s.brands_touched} brand${s.brands_touched === 1 ? "" : "s"} touched · ${s.manual} manual override${s.manual === 1 ? "" : "s"} · ${s.carry_over} carry-over${s.carry_over === 1 ? "" : "s"}`}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {[7, 30, 90].map(w => (
            <button key={w} type="button" onClick={() => setDays(w)}
                      className={`h-6 px-2 text-[10px] border ${days === w ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:border-primary"}`}
                      data-testid={`portfolio-scf-window-${w}`}>
              {w}d
            </button>
          ))}
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <KpiTile icon={PenSquare} label="Manual overrides"
                   value={s.manual} tone="amber"
                   testid="portfolio-scf-kpi-manual" />
        <KpiTile icon={ArrowLeftRight} label="Carry-overs"
                   value={s.carry_over} tone="violet"
                   testid="portfolio-scf-kpi-carry" />
        <KpiTile icon={Bot} label="AI regenerations"
                   value={s.ai_regens} tone="sky"
                   testid="portfolio-scf-kpi-ai" />
        <KpiTile icon={ShieldCheck} label="Brands touched"
                   value={s.brands_touched} tone="emerald"
                   testid="portfolio-scf-kpi-brands" />
      </div>

      {/* Sparkline series */}
      {!isQuiet && (
        <div className="border border-border bg-muted/10 p-2 mb-3">
          <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
            Daily edits · last {days} days
          </div>
          <div className="flex items-end gap-0.5 h-16">
            {data.series.map((d, i) => {
              const total = d.manual + d.carry_over + d.ai_regens;
              const hManual = (d.manual / sparklineMax) * 100;
              const hCarry = (d.carry_over / sparklineMax) * 100;
              const hAi = (d.ai_regens / sparklineMax) * 100;
              return (
                <div key={i} className="flex-1 h-full flex flex-col justify-end"
                        title={`${d.date}\n${d.manual} manual · ${d.carry_over} carry · ${d.ai_regens} AI (${total})`}
                        data-testid={`portfolio-scf-day-${d.date}`}>
                  {hAi > 0 && <div className="bg-sky-500" style={{ height: `${hAi}%` }} />}
                  {hCarry > 0 && <div className="bg-violet-500" style={{ height: `${hCarry}%` }} />}
                  {hManual > 0 && <div className="bg-amber-500" style={{ height: `${hManual}%` }} />}
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-3 mt-1 text-[9px]">
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-amber-500 inline-block" /> Manual</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-violet-500 inline-block" /> Carry-over</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-sky-500 inline-block" /> AI regen</span>
          </div>
        </div>
      )}

      {/* Top brands + Top editors */}
      {!isQuiet && (
        <div className="grid grid-cols-1 md:grid-cols-[1fr_260px] gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
              Top brands · click to see revisions
            </div>
            <div className="space-y-1">
              {data.top_brands.map(b => (
                <button key={b.brand_id} type="button"
                            onClick={() => { setDrawerBrandId(b.brand_id); setDrawerBrandName(b.brand_name); }}
                            className="w-full flex items-center gap-2 text-xs px-2 py-1.5 border border-border bg-white hover:border-primary text-left"
                            data-testid={`portfolio-scf-brand-${b.brand_id}`}>
                  <span className="font-medium truncate flex-1">{b.brand_name}</span>
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono text-amber-800 bg-amber-50 border border-amber-300 px-1">
                    <PenSquare className="w-2.5 h-2.5" /> {b.manual}
                  </span>
                  {b.carry_over > 0 && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-mono text-violet-800 bg-violet-50 border border-violet-300 px-1">
                      <ArrowLeftRight className="w-2.5 h-2.5" /> {b.carry_over}
                    </span>
                  )}
                  <span className="text-[10px] text-muted-foreground">
                    {b.last_editor_name} · {fmtRelative(b.last_edit_at)}
                  </span>
                  <ChevronRight className="w-3 h-3 text-muted-foreground" />
                </button>
              ))}
              {data.top_brands.length === 0 && (
                <div className="text-[11px] text-muted-foreground italic p-2">No brands touched.</div>
              )}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1 flex items-center gap-1">
              <Users className="w-3 h-3" /> Top editors this window
            </div>
            <div className="space-y-1">
              {data.top_editors.map(e => (
                <div key={e.id} className="flex items-center gap-2 text-xs px-2 py-1.5 border border-border bg-white"
                          data-testid={`portfolio-scf-editor-${e.id}`}>
                  <UserIcon className="w-3 h-3 text-muted-foreground" />
                  <span className="font-medium truncate flex-1">{e.name}</span>
                  <span className={`text-[9px] uppercase tracking-widest font-semibold border px-1
                                          ${e.role_hint === "client"
                                              ? "border-rose-400 text-rose-800 bg-rose-50"
                                              : "border-sky-400 text-sky-800 bg-sky-50"}`}>
                    {e.role_hint}
                  </span>
                  <span className="text-[10px] font-mono text-muted-foreground">{e.edits}</span>
                </div>
              ))}
              {data.top_editors.length === 0 && (
                <div className="text-[11px] text-muted-foreground italic p-2">No manual editors.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mini-drawer with revisions */}
      <BrandRevisionsDrawer
        open={!!drawerBrandId}
        onOpenChange={(v) => { if (!v) setDrawerBrandId(null); }}
        wsId={wsId}
        brandId={drawerBrandId}
        brandName={drawerBrandName}
      />
    </div>
  );
}

function KpiTile({ icon: Icon, label, value, tone, testid }) {
  const t = {
    amber: "border-amber-400 bg-amber-50 text-amber-900",
    violet: "border-violet-400 bg-violet-50 text-violet-900",
    sky: "border-sky-400 bg-sky-50 text-sky-900",
    emerald: "border-emerald-400 bg-emerald-50 text-emerald-900",
  }[tone] || "border-border bg-card";
  return (
    <div className={`border p-2 ${t}`} data-testid={testid}>
      <div className="text-[9px] uppercase tracking-widest font-bold flex items-center gap-1">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="text-2xl font-mono font-black">{value ?? 0}</div>
    </div>
  );
}

function BrandRevisionsDrawer({ open, onOpenChange, wsId, brandId, brandName }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !brandId) return;
    setLoading(true);
    api.get(`/workspaces/${wsId}/brands/${brandId}/content-strategy/latest-revisions`,
              { params: { limit: 40 } })
      .then(r => setRows(r.data.rows || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [open, wsId, brandId]);

  const renderRow = (r) => {
    const kind = r.kind || r.source || "ai";
    const isAI = kind === "ai_generate" || r.source === "ai";
    const isCarry = kind === "carry_over" || r.source === "carry_over";
    const Icon = isAI ? Bot : isCarry ? ArrowLeftRight : PenSquare;
    const tone = isAI ? "border-l-sky-500 bg-sky-50/40"
                      : isCarry ? "border-l-violet-500 bg-violet-50/40"
                                : "border-l-amber-500 bg-amber-50/40";
    const section = r.section === "*all*" ? "All sections" : (r.section || "—");
    return (
      <div key={r.id} className={`border border-border border-l-4 ${tone} p-2`}
                data-testid={`scf-drawer-rev-${r.id}`}>
        <div className="flex items-center gap-2">
          <Icon className="w-3.5 h-3.5 text-primary shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold truncate">
              {isAI ? "AI generated" : isCarry ? "Carried over" : "Manual edit"} · {section}
              {r.version != null && <span className="text-[10px] text-muted-foreground ml-1">· v{r.version}</span>}
            </div>
            <div className="text-[10px] text-muted-foreground truncate">
              {r.actor_name || "system"} · {fmtRelative(r.at)}
              {r.carried_from_version && ` · from v${r.carried_from_version}`}
              {r.note && ` · "${r.note}"`}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[520px] sm:max-w-[560px] overflow-y-auto"
                        data-testid="scf-brand-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ScrollText className="w-4 h-4 text-primary" />
            {brandName || "Brand"} · Strategy revisions
          </SheetTitle>
        </SheetHeader>
        <div className="mt-3 space-y-1.5">
          {loading && <div className="text-sm text-muted-foreground p-6 text-center">Loading…</div>}
          {!loading && rows.length === 0 && (
            <div className="text-sm text-muted-foreground p-6 text-center border border-dashed border-border">
              No revisions found for this brand.
            </div>
          )}
          {!loading && rows.map(renderRow)}
        </div>
      </SheetContent>
    </Sheet>
  );
}
